import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiBookOpen, FiUser, FiCalendar, FiUsers, FiSearch, FiShoppingCart, FiInfo, FiPlus, FiMinus, FiChevronDown, FiChevronUp, FiSliders } from 'react-icons/fi';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import styles from './CourseCatalog.module.css';

const SORT_OPTIONS = {
  newest: 'newest',
  oldest: 'oldest',
  nameAsc: 'name-asc',
  nameDesc: 'name-desc'
};

const getCourseDateValue = (course, field) => {
  const dateValue = course[field] || course[field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)];
  if (!dateValue) return 0;

  const timestamp = new Date(dateValue).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortCourses = (courses, sortBy) => {
  return [...courses].sort((a, b) => {
    if (sortBy === SORT_OPTIONS.nameAsc) {
      return a.name.localeCompare(b.name);
    }

    if (sortBy === SORT_OPTIONS.nameDesc) {
      return b.name.localeCompare(a.name);
    }

    const aStartDate = getCourseDateValue(a, 'startDate');
    const bStartDate = getCourseDateValue(b, 'startDate');
    const aEndDate = getCourseDateValue(a, 'endDate');
    const bEndDate = getCourseDateValue(b, 'endDate');

    if (sortBy === SORT_OPTIONS.oldest) {
      return aStartDate - bStartDate || aEndDate - bEndDate || a.name.localeCompare(b.name);
    }

    return bStartDate - aStartDate || bEndDate - aEndDate || a.name.localeCompare(b.name);
  });
};

const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  // Parse date-only strings without timezone conversion.
  const [year, month, day] = dateString.split('T')[0].split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

const parseCourseDate = (dateString) => {
  if (!dateString) return null;

  const [year, month, day] = String(dateString).split('T')[0].split('-');
  if (!year || !month || !day) return null;

  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return Number.isNaN(date.getTime()) ? null : date;
};

const isPreviousCourse = (course) => {
  const endDate = parseCourseDate(course.endDate || course.end_date);
  if (!endDate) return false;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return endDate < today;
};

function CourseCard({
  course,
  isSelected,
  isEnrolled,
  isExpanded,
  onToggleSelection,
  onToggleExpansion
}) {
  const descRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const element = descRef.current;
      if (!element) return;

      const previousLineClamp = element.style.webkitLineClamp;
      const previousOverflow = element.style.overflow;
      const previousDisplay = element.style.display;

      element.style.display = '-webkit-box';
      element.style.webkitLineClamp = '3';
      element.style.overflow = 'hidden';
      const collapsedHeight = element.clientHeight;

      element.style.webkitLineClamp = 'unset';
      element.style.overflow = 'visible';
      element.style.display = 'block';
      const fullHeight = element.scrollHeight;

      element.style.webkitLineClamp = previousLineClamp;
      element.style.overflow = previousOverflow;
      element.style.display = previousDisplay;

      setIsTruncated(fullHeight > collapsedHeight + 1);
    };

    const frameId = requestAnimationFrame(checkTruncation);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(checkTruncation);
    }

    return () => cancelAnimationFrame(frameId);
  }, [course.description]);

  return (
    <motion.div
      className={`${styles.catalogCard} ${isSelected ? styles.selected : ''}`}
      initial={false}
    >
      <div className={styles.cardHeader}>
        {!isEnrolled && !course.isPaidCourse && (
          <span className={styles.freeBadge}>Free</span>
        )}
        <div className={styles.courseCode}>
          {course.code}
        </div>
        <h3 className={styles.cardTitle}>
          {course.name}
        </h3>
        <div className={styles.descriptionWrapper}>
          <p
            ref={descRef}
            className={`${styles.cardDescription} ${isExpanded ? styles.expanded : ''}`}
          >
            {course.description || 'No description available.'}
          </p>
          {isTruncated && (
            <button
              className={styles.readMoreButton}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpansion(course.id);
              }}
              type="button"
            >
              {isExpanded ? (
                <>
                  <FiChevronUp size={14} />
                  Show less
                </>
              ) : (
                <>
                  <FiChevronDown size={14} />
                  Show more
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className={styles.cardMeta}>
        <div className={styles.metaItem}>
          <FiUser size={14} />
          <span>{course.instructor || 'TBA'}</span>
        </div>
        <div className={styles.metaItem}>
          <FiCalendar size={14} />
          <span>{course.academicYear || course.academic_year || '2025-2026'}</span>
        </div>
        <div className={styles.metaItem}>
          <FiBookOpen size={14} />
          <span>{formatDate(course.startDate || course.start_date)} - {formatDate(course.endDate || course.end_date)}</span>
        </div>
        <div className={styles.metaItem}>
          <FiUsers size={14} />
          <span>{course.maxStudents || course.max_students ? `${course.maxStudents || course.max_students} students max` : 'Unlimited'}</span>
        </div>
      </div>

      <div className={styles.cardFooter}>
        <div className={styles.priceInfo}>
          <span className={styles.priceLabel}>
            {course.isPaidCourse ? 'Price' : 'Free Course'}
          </span>
          <span className={styles.price}>
            {course.isPaidCourse ? `$${course.price ? parseFloat(course.price).toFixed(2) : '0.00'}` : 'Free'}
          </span>
        </div>
        {isEnrolled ? (
          <div className={styles.enrolledBadge}>ENROLLED</div>
        ) : (
          <button
            className={`${styles.ctaButton} ${isSelected ? styles.selected : ''}`}
            disabled={isEnrolled}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelection(course);
            }}
          >
            {isSelected ? (
              <>
                <FiMinus size={16} />
                <span className={styles.buttonText}>Remove from Cart</span>
              </>
            ) : (
              <>
                <FiPlus size={16} />
                <span className={styles.buttonText}>Add to Cart</span>
              </>
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
}

function CourseCatalog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  // Check if user came from login/signup with redirect=/catalog
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const redirectFromStorage = typeof window !== 'undefined' 
    ? localStorage.getItem('faithinsight_login_redirect') 
    : null;
  
  // Show message if redirect parameter is /catalog in URL or was stored in localStorage
  const showRedirectMessage = redirectParam === '/catalog' || redirectFromStorage === '/catalog';
  
  // Clear the redirect from localStorage and URL after showing the message
  useEffect(() => {
    if (showRedirectMessage) {
      // Clear from localStorage
      if (redirectFromStorage === '/catalog') {
        localStorage.removeItem('faithinsight_login_redirect');
      }
      
      // Clear from URL query parameters (replace state to remove ?redirect=/catalog from URL)
      if (redirectParam === '/catalog') {
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('redirect');
        const newSearch = newSearchParams.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [showRedirectMessage, redirectFromStorage, redirectParam, location.search, location.pathname]);
  const [courses, setCourses] = useState([]);
  const [additionalProducts, setAdditionalProducts] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.newest);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [bulkPricing, setBulkPricing] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const activeCourses = filteredCourses.filter(course => !isPreviousCourse(course));
  const previousCourses = filteredCourses.filter(isPreviousCourse);

  // Fetch user enrollments
  const fetchUserEnrollments = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await api.getUserEnrollments();
      setUserEnrollments(response.enrollments || []);
    } catch (error) {
      logger.error('Error fetching user enrollments', { error });
      // Don't set error state for enrollments, just log it
    }
  }, [isAuthenticated]);

  // Fetch courses and additional products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch courses (now includes pricing information)
        const coursesResponse = await api.getPublicCourses();
        const coursesData = coursesResponse.courses || [];

        // Ensure all courses have pricing fields with defaults
        const coursesWithPricing = coursesData.map((course) => ({
          ...course,
          price: course.price ?? 0,
          currency: course.currency || 'USD',
          isPaidCourse: course.isPaidCourse ?? false
        }));

        // Fetch additional products
        const productsResponse = await api.getActiveAdditionalProducts();
        const productsData = productsResponse.products || [];

        setCourses(coursesWithPricing);
        setAdditionalProducts(productsData);
        setFilteredCourses(sortCourses(coursesWithPricing, SORT_OPTIONS.newest));
        setFilteredProducts(productsData);
        
        // Fetch user enrollments if authenticated
        if (isAuthenticated) {
          await fetchUserEnrollments();
        }
      } catch (error) {
        logger.error('Error fetching data', { error });
        setError('Failed to load courses and products');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated, user, fetchUserEnrollments]);

  // Filter courses and products based on search term and price filter
  useEffect(() => {
    let filteredCourses = courses;
    let filteredProducts = additionalProducts;

    // Filter by search term
    if (searchTerm) {
      filteredCourses = filteredCourses.filter(course =>
        course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      filteredProducts = filteredProducts.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }


    setFilteredCourses(sortCourses(filteredCourses, sortBy));
    setFilteredProducts(filteredProducts);
  }, [courses, additionalProducts, searchTerm, sortBy]);


  const handleMultiEnroll = async () => {
    // Pre-fetch CSRF token before navigating to prevent "CSRF token is invalid or expired" errors
    try {
      await api.fetchCSRFToken();
    } catch (error) {
      logger.warn('Failed to pre-fetch CSRF token before checkout', { error });
      // Continue with navigation anyway - PaymentForm will try to fetch it again
    }
    
    // If only courses are selected, go directly to payment form
    if (selectedCourses.length > 0 && selectedProducts.length === 0) {
      const courseCodes = selectedCourses.map(course => course.code);
      navigate(`/enroll/multiple/${courseCodes.join(',')}`, { state: { scrollToTop: true } });
      return;
    }
    
    // If only products are selected, go directly to payment form
    if (selectedProducts.length > 0 && selectedCourses.length === 0) {
      const productIds = selectedProducts.map(product => product.id);
      navigate(`/purchase/products/${productIds.join(',')}`, { state: { scrollToTop: true } });
      return;
    }
    
    // If both courses and products are selected, go to mixed purchase flow
    if (selectedCourses.length > 0 && selectedProducts.length > 0) {
      const courseCodes = selectedCourses.map(course => course.code);
      const productIds = selectedProducts.map(product => product.id);
      navigate(`/enroll/mixed/${courseCodes.join(',')}/${productIds.join(',')}`, { state: { scrollToTop: true } });
      return;
    }
  };

  const toggleCourseSelection = (course) => {
    setSelectedCourses(prev => {
      const isSelected = prev.some(selected => selected.id === course.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== course.id);
      } else {
        return [...prev, course];
      }
    });
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.some(selected => selected.id === product.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  // Calculate bulk pricing when courses are selected
  useEffect(() => {
    const calculateBulkPricing = async () => {
      // Only calculate bulk pricing if 2+ courses are selected and user is authenticated
      if (selectedCourses.length >= 2 && isAuthenticated) {
        try {
          const courseIds = selectedCourses
            .filter(course => course.isPaidCourse)
            .map(course => parseInt(course.id));
          
          if (courseIds.length >= 2) {
            const response = await api.calculateBulkPricing(courseIds);
            if (response.success && response.bulkPricing) {
              setBulkPricing(response);
            } else {
              setBulkPricing(null);
            }
          } else {
            setBulkPricing(null);
          }
        } catch (error) {
          logger.error('Error calculating bulk pricing', { error });
          setBulkPricing(null);
        }
      } else {
        setBulkPricing(null);
      }
    };

    calculateBulkPricing();
  }, [selectedCourses, isAuthenticated]);

  // Check if user is enrolled in a course
  const isEnrolledInCourse = (courseId) => {
    return courses.some(course => course.id === courseId && course.isEnrolled) ||
      userEnrollments.some(enrollment => enrollment.course_id === courseId);
  };


  const getTotalPrice = () => {
    const courseTotal = selectedCourses.reduce((total, course) => {
      return total + (course.isPaidCourse ? parseFloat(course.price) : 0);
    }, 0);
    
    const productTotal = selectedProducts.reduce((total, product) => {
      return total + parseFloat(product.price || 0);
    }, 0);
    
    const subtotal = courseTotal + productTotal;
    
    // Apply bulk pricing discount if available
    if (bulkPricing && bulkPricing.pricing) {
      // Only apply bulk pricing discount to courses, not products
      const courseDiscount = bulkPricing.pricing.discountAmount || 0;
      return Math.max(0, subtotal - courseDiscount);
    }
    
    return subtotal;
  };

  const getOriginalTotal = () => {
    const courseTotal = selectedCourses.reduce((total, course) => {
      return total + (course.isPaidCourse ? parseFloat(course.price) : 0);
    }, 0);
    
    const productTotal = selectedProducts.reduce((total, product) => {
      return total + parseFloat(product.price || 0);
    }, 0);
    
    return courseTotal + productTotal;
  };

  const toggleCourseDescription = (courseId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [`course-${courseId}`]: !prev[`course-${courseId}`]
    }));
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  const hasSelectedItems = selectedCourses.length > 0 || selectedProducts.length > 0;

  return (
    <>
      <div className={`${styles.container} ${hasSelectedItems ? styles.hasSelectedItems : ''}`}>
        <div className={styles.contentContainer}>
          <div className={styles.pageHeader}>
            <div className={styles.headerContent}>
              <div className={styles.headerText}>
                <h1 className={styles.catalogTitle}>Course Catalog</h1>
                <p className={styles.catalogSubtitle}>
                  Learn the essentials of Aqeedah and Fiqh through structured courses that support your spiritual growth.
                </p>
              </div>
            </div>
            <div className={styles.filterControls}>
              <div className={styles.searchContainer}>
                <FiSearch className={styles.searchIcon} size={20} />
                <input
                  className={styles.searchInput}
                  type="text"
                  placeholder="Search catalog"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <label className={styles.sortContainer} aria-label="Sort courses">
                <FiSliders className={styles.sortIcon} size={18} />
                <select
                  className={styles.sortSelect}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value={SORT_OPTIONS.newest}>Newest first</option>
                  <option value={SORT_OPTIONS.oldest}>Oldest first</option>
                  <option value={SORT_OPTIONS.nameAsc}>A - Z</option>
                  <option value={SORT_OPTIONS.nameDesc}>Z - A</option>
                </select>
              </label>
            </div>
          </div>

      {showRedirectMessage && (
        <motion.div
          className={styles.redirectMessage}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className={styles.messageIcon}>
            <FiInfo size={24} />
          </div>
          <p className={styles.messageText}>
            Choose your courses and proceed to checkout to complete your registration.
          </p>
        </motion.div>
      )}

      <motion.div
        className={styles.offerBanner}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <FiInfo className={styles.bannerIcon} size={18} />
        <span className={styles.bannerText}>
          <strong>Special offer:</strong> Enroll in 2 courses and get <strong>$20 off</strong> (active courses only). Discount will be applied automatically at checkout.
        </span>
      </motion.div>

      {filteredCourses.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <FiBookOpen />
          </div>
          <h3>No courses found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      ) : (
        <div className={styles.courseGroups}>
          {activeCourses.length > 0 && (
            <section className={styles.courseGroup} aria-labelledby="active-courses-heading">
              <div className={styles.courseGroupHeader}>
                <h2 id="active-courses-heading" className={styles.courseGroupTitle}>Active Courses</h2>
              </div>
              <div className={styles.catalogGrid}>
                {activeCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    isSelected={selectedCourses.some(selected => selected.id === course.id)}
                    isEnrolled={isEnrolledInCourse(course.id)}
                    isExpanded={Boolean(expandedDescriptions[`course-${course.id}`])}
                    onToggleSelection={toggleCourseSelection}
                    onToggleExpansion={toggleCourseDescription}
                  />
                ))}
              </div>
            </section>
          )}

          {previousCourses.length > 0 && (
            <section className={styles.courseGroup} aria-labelledby="previous-courses-heading">
              <div className={styles.courseGroupHeader}>
                <h2 id="previous-courses-heading" className={styles.courseGroupTitle}>Previous Courses</h2>
              </div>
              <div className={styles.catalogGrid}>
                {previousCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    isSelected={selectedCourses.some(selected => selected.id === course.id)}
                    isEnrolled={isEnrolledInCourse(course.id)}
                    isExpanded={Boolean(expandedDescriptions[`course-${course.id}`])}
                    onToggleSelection={toggleCourseSelection}
                    onToggleExpansion={toggleCourseDescription}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Additional Products Section */}
      {filteredProducts.length > 0 && (
        <>
          <section className={`${styles.courseGroup} ${styles.sponsorshipSection}`} aria-labelledby="sponsorship-heading">
            <div className={styles.courseGroupHeader}>
              <h2 id="sponsorship-heading" className={styles.courseGroupTitle}>Sponsorship</h2>
            </div>
            <div className={styles.catalogGrid}>
            {filteredProducts.map((product) => {
              const isSelected = selectedProducts.some(selected => selected.id === product.id);
              return (
                <motion.div
                  key={product.id}
                  className={`${styles.catalogCard} ${isSelected ? styles.selected : ''}`}
                  initial={false}
                >
                  <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>
                      {product.product_type === 'merchandise' ? (
                        <FiShoppingCart size={16} />
                      ) : product.product_type === 'curriculum_package' ? (
                        <FiBookOpen size={16} />
                      ) : (
                        <FiShoppingCart size={16} />
                      )}
                      {product.name}
                      {product.product_type === 'curriculum_package' && <span className={styles.featuredBadge}>Featured</span>}
                    </h3>
                    <p className={styles.cardDescription}>
                      {product.description || 'No description available.'}
                    </p>
                  </div>

                  <div className={styles.cardMeta}>
                    {/* Maintains card height consistency */}
                  </div>

                  <div className={styles.cardFooter}>
                    <div className={styles.priceInfo}>
                      <span className={styles.priceLabel}>Price</span>
                      <span className={styles.price}>
                        {`$${product.price ? parseFloat(product.price).toFixed(2) : '0.00'}`}
                      </span>
                    </div>
                    <button
                      className={`${styles.ctaButton} ${isSelected ? styles.selected : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleProductSelection(product);
                      }}
                    >
                      {isSelected ? (
                        <>
                          <FiMinus size={16} />
                          Remove from Cart
                        </>
                      ) : (
                        <>
                          <FiPlus size={16} />
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              );
            })}
            </div>
          </section>
        </>
      )}
        </div>
      </div>

      {/* Sticky Checkout Bar - Only show when items are selected */}
      {hasSelectedItems && (
        <div className={styles.multiSelectControls}>
          <div className={styles.cartSummary}>
            <div className={styles.cartInfo}>
              <div className={styles.cartCount}>
                {selectedCourses.length + selectedProducts.length} item{(selectedCourses.length + selectedProducts.length) !== 1 ? 's' : ''} selected
                {selectedCourses.length > 0 && ` (${selectedCourses.length} course${selectedCourses.length !== 1 ? 's' : ''})`}
                {selectedProducts.length > 0 && ` (${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''})`}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem' }}>
                {bulkPricing && bulkPricing.pricing && bulkPricing.pricing.discountAmount > 0 && (
                  <>
                    <span className={styles.cartOriginalTotal}>${getOriginalTotal().toFixed(2)}</span>
                    <span className={styles.cartDiscount}>
                      (Save ${bulkPricing.pricing.discountAmount.toFixed(2)})
                    </span>
                  </>
                )}
                <span className={styles.cartTotal}>Total: ${getTotalPrice().toFixed(2)}</span>
              </div>
            </div>
            <button 
              className={styles.enrollAllButton}
              onClick={handleMultiEnroll}
              disabled={selectedCourses.length === 0 && selectedProducts.length === 0}
            >
              <FiShoppingCart size={16} />
              Checkout
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default CourseCatalog;
